import { combineLatest, from, Observable, of, throwError } from "rxjs";
import { map, mergeMap, take, tap } from "rxjs/operators";
import { decodeMethodReturn } from 'web3-eth-contract';
import { keccak256 } from 'js-sha3';
import { AccountService } from "../services/account.service";
import { ContractService, Instances } from "../services/contract.service";
import { getPaymasterParams } from "./paymaster-utils";
import { DEFAULT_GAS_PER_PUBDATA_LIMIT, serialize } from "./eip712-signer";
import BigNumber from "bignumber.js";
import { Network } from "../services/configuration";
import { PaymasterMethod } from "./paymaster-method";
import * as ethUtil from 'ethereumjs-util';
import { Token } from "./token";
import { environment } from "src/environments/environment";

let feeDataTimestamp = 0;
let feeData: any = null;

export class Base {
  public instances: Instances;

  constructor(
    protected contract: string,
    protected abi: any,
    protected accountService: AccountService,
    protected contractService: ContractService
  ) {
    this.instances = this.contractService.getInstances(this.contract, abi);
  }

  setReadonlyNetwork(network: Network) {
    this.instances.setReadonlytNetwork(network);
  }

  private rawCall(func: (i: any) => any, options = {}) {
    return this.instances.readonlyInstance.pipe(
      take(1),
      mergeMap((i) => from(func(i).call(options)) as Observable<any>)
    );
  }

  private rawSend(func: (i: any, a: any) => any, options: any = {}) {
    return combineLatest([this.instances.instance, this.accountService.account]).pipe(
      take(1),
      mergeMap(([i, a]) => from(func(i, a).send({
        from: a,
        ...options
      })))
    );
  }

  protected send(func: (i: any, a: any) => any, { paymasterMethod, extraData, ...options }: any = {}) {
    return this.injectOptions(options).pipe(
      mergeMap((o) => {
        if (paymasterMethod) {
          return this.sendByPaymaster(func, paymasterMethod, extraData, o);
        } else if (extraData) {
          return this.sendWithData(func, extraData, o);
        } else {
          return this.rawSend(func, o)
        }
      })
    );
  }

  protected call(func: (i: any) => any, { extraData, ...options }: any = {}) {
    if (extraData) {
      return this.callWithData(func, extraData, options);
    } else {
      return this.rawCall(func, options)
    }
  }

  protected estimateGas(func: (i: any, a: any) => any, { paymasterMethod, extraData, ...options }: any = {}): Observable<any> {
    return this.injectOptions(options).pipe(
      mergeMap((o) => {
        if (paymasterMethod) {
          return this.estimateGasByPaymaster(func, paymasterMethod, extraData, options);
        } else if (extraData) {
          return this.estimateGasWithData(func, extraData, options);
        } else {
          return this.rawEstimateGas(func, options)
        }
      })
    );
  }

  private rawEstimateGas(func: (i: any, a: any) => any, options: any = {}) {
    return combineLatest([this.instances.instance, this.accountService.account]).pipe(
      take(1),
      mergeMap(([i, a]) => from(func(i, a).estimateGas({
        from: a,
        ...options
      })))
    );
  }

  private checkBalanceEnough(token: Token, required: any) {
    return combineLatest([token.balance, token.symbol]).pipe(
      take(1),
      mergeMap(([b, s]) => {
        if (b.times(token.decimalsFactor).gt(required)) {
          return of(true);
        } else {
          return throwError(new Error(`Can't pay gas by ${s}, balance is insufficient.`));
        }
      })
    );
  }

  private estimateGasByPaymasterNormal(func: (i: any, a: any) => any, paymasterMethod: PaymasterMethod, extraData?: string, options = {}) {
    return combineLatest([this.instances.instance, this.accountService.account, this.accountService.getNonce(), this.contractService.ethWalletService.chainId, paymasterMethod.token.balance]).pipe(
      take(1),
      mergeMap(([i, a, n, chainId, b]) => {
        const paymasterParams = getPaymasterParams(paymasterMethod.paymaster, {
          type: 'ApprovalBased',
          token: paymasterMethod.token.contract,
          minimalAllowance: new BigNumber('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff', 16).toString(10),
          innerInput: '0x'
        });

        const to = i._address;
        let input = func(i, a).encodeABI();
        if (extraData) {
          input += extraData;
        }
        let tx: any =  {
          data: input,
          to: to,
          from: a,
          chainId: chainId,
          nonce: Number(n),
          type: 113,
          customData: {
            gasPerPubdata: DEFAULT_GAS_PER_PUBDATA_LIMIT,
            paymasterParams
          },
          value: 0,
          ...options
        };

        return combineLatest([this.contractService.ethWalletService.estimateGas(tx), this.contractService.ethWalletService.getGasPrice()]).pipe(
          mergeMap(([gas, gasPrice]: any) => {
            tx.gasLimit = Number(gas) + 65000;
            tx.gasPrice = gasPrice;
            const minimalAllowance = paymasterMethod.tokenPrice.times(1.05).times(paymasterMethod.token.decimalsFactor).times(tx.gasLimit).times(gasPrice).fromDecimal().toFixed(0);

            return this.checkBalanceEnough(paymasterMethod.token, minimalAllowance).pipe(
              map(() => {
                tx.customData.paymasterParams = getPaymasterParams(paymasterMethod.paymaster, {
                  type: 'ApprovalBased',
                  token: paymasterMethod.token.contract,
                  minimalAllowance,
                  innerInput: '0x'
                });
                return [tx, new BigNumber(minimalAllowance).div(paymasterMethod.token.decimalsFactor)];
              })
            );
          })
        );
      })
    );
  }

  private estimateGasByPaymasterHoldstation(func: (i: any, a: any) => any, paymasterMethod: PaymasterMethod, extraData?: string, options = {}) {
    const holdstationPaymaster = paymasterMethod.data;
    const partnerCode = '0x' + ethUtil.fromAscii('ZOMMA').substring(2).padEnd(64, '0');
    return combineLatest([this.instances.instance, this.accountService.account, this.accountService.getNonce(), this.contractService.ethWalletService.chainId, paymasterMethod.token.balance]).pipe(
      take(1),
      mergeMap(([i, a, n, chainId, b]) => {
        const paymasterParams = getPaymasterParams(paymasterMethod.paymaster, {
          type: 'ApprovalBased',
          token: paymasterMethod.token.contract,
          minimalAllowance: new BigNumber('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff', 16).toString(10),
          innerInput: partnerCode
        });

        const to = i._address;
        let input = func(i, a).encodeABI();
        if (extraData) {
          input += extraData;
        }
        let tx: any =  {
          data: input,
          to: to,
          from: a,
          chainId: chainId,
          nonce: Number(n),
          type: 113,
          customData: {
            gasPerPubdata: DEFAULT_GAS_PER_PUBDATA_LIMIT,
            paymasterParams
          },
          value: 0,
          ...options
        };

        return combineLatest([this.contractService.ethWalletService.estimateGas(tx), this.contractService.ethWalletService.getGasPrice()]).pipe(
          mergeMap(([gas, gasPrice]: any) => (holdstationPaymaster.getTokenFee(paymasterMethod.token.contract, new BigNumber(gas).times(1.5).times(gasPrice).fromDecimal()) as Observable<any>).pipe(
            mergeMap((result) => {
              const afterDiscount = result[0];
              const minAmount = result[1];
              tx.gasLimit = (Number(gas) * 1.5).toFixed(0);
              tx.gasPrice = gasPrice;
              const minimalAllowance = new BigNumber(minAmount).times(1.05).toFixed(0);
              return this.checkBalanceEnough(paymasterMethod.token, minimalAllowance).pipe(
                map(() => {
                  tx.customData.paymasterParams = getPaymasterParams(paymasterMethod.paymaster, {
                    type: 'ApprovalBased',
                    token: paymasterMethod.token.contract,
                    minimalAllowance,
                    innerInput: partnerCode
                  });
                  return [tx, new BigNumber(afterDiscount).div(paymasterMethod.token.decimalsFactor)];
                })
              );
            })
          ))
        );
      })
    );
  }

  private estimateGasByPaymaster(func: (i: any, a: any) => any, paymasterMethod: PaymasterMethod, extraData?: string, options = {}) {
    const method = paymasterMethod.type === 'holdstation' ? this.estimateGasByPaymasterHoldstation : this.estimateGasByPaymasterNormal
    return method.bind(this)(func, paymasterMethod, extraData, options);
  }

  private sendByPaymaster(func: (i: any, a: any) => any, paymasterMethod: PaymasterMethod, extraData?: string, options = {}) {
    return this.estimateGasByPaymaster(func, paymasterMethod, extraData, options).pipe(
      mergeMap(([tx]) => this.contractService.ethWalletService.signAa(tx).pipe(
        mergeMap((customSignature) => {
          tx.customData = {
            ...tx.customData,
            customSignature,
          } as any;
          return this.contractService.ethWalletService.sendRawTransaction([serialize(tx)]);
        }),
        mergeMap((txHash: any) => this.contractService.ethWalletService.waitForTransactionConfirmed(txHash))
      ))
    );
  }

  private callWithData(func: (i: any) => any, extraData: any, options = {}) {
    return this.instances.readonlyInstance.pipe(
      take(1),
      mergeMap((i) => {
        const to = i._address;
        const input = func(i).encodeABI();
        const signature = input.substr(0, 10);
        return this.contractService.ethWalletService.call({
          to,
          data: input + extraData,
          ...options
        }).pipe(
          map((returnData: any) => {
            return decodeMethodReturn(i._jsonInterface.find((d: any) => d.signature === signature), returnData);
            // return i._decodeMethodReturn(i._jsonInterface.find((d: any) => d.signature === signature).outputs, returnData);
          })
        );
      })
    );
  }

  private sendWithData(func: (i: any, a: any) => any, extraData: any, options = {}) {
    return combineLatest([this.instances.instance, this.accountService.account]).pipe(
      take(1),
      mergeMap(([i, a]) => {
        const to = i._address;
        const input = func(i, a).encodeABI();
        return this.contractService.ethWalletService.sendTransaction({
          to,
          from: a,
          data: input + extraData,
          ...options
        });
      })
    );
  }

  private estimateGasWithData(func: (i: any, a: any) => any, extraData: any, options = {}) {
    return combineLatest([this.instances.instance, this.accountService.account]).pipe(
      take(1),
      mergeMap(([i, a]) => {
        const to = i._address;
        const input = func(i, a).encodeABI();
        return this.contractService.ethWalletService.estimateGas({
          to,
          from: a,
          data: input + extraData,
          ...options
        });
      })
    );
  }

  protected createTypes(types: any) {
    return {
      EIP712Domain: [
        {
          name: 'name',
          type: 'string',
        },
        {
          name: 'version',
          type: 'string',
        },
        {
          name: 'chainId',
          type: 'uint256',
        },
        {
          name: 'verifyingContract',
          type: 'address',
        }
      ],
      ...types
    };
  }

  protected tryParseCustomError(e: any, otherAbis: any[] = []) {
    const data = e?.data?.data || e?.innerError?.data;
    let methodId = '';
    if (typeof data === 'string' && data.startsWith('0x')) {
      methodId = data.substring(2, 10);
    }
    return this.instances.readonlyInstance.pipe(
      mergeMap((i) => {
        if (methodId) {
          this.injectCustomError(data, methodId, e, i, otherAbis);
        }
        return throwError(e);
      })
    );
  }

  private injectCustomError(data: string, methodId: string, error: any, instance: any, otherAbis: any[] = []) {
    otherAbis.unshift(instance._errorsInterface)
    let obj;
    for (let abi of otherAbis) {
      obj = this.findError(abi, methodId);
      if (obj) {
        break;
      }
    }
    if (obj) {
      let decodedReturn: any = decodeMethodReturn({ outputs: obj.inputs } as any, `0x${data.substring(10)}`);
      if (!Array.isArray(decodedReturn)) {
        decodedReturn = [decodedReturn];
      }
      const dataObj: any = {};
      obj.inputs.forEach((input: any, i: number) => {
        dataObj[input.name] = decodedReturn[i];
      })
      error.customError = {
        name: obj.name,
        data: dataObj,
        dataArray: decodedReturn
      };
    }
  }

  private findError(abi: any[], methodId: string, ) {
    return abi.find((obj: any) => {
      if (!obj.signature) {
        const args = obj.inputs.map((input: any) => input.type).join(',');
        obj.signature = keccak256(`${obj.name}(${args})`).substring(0, 8);
      }
      return obj.signature === methodId;
    });
  }

  private injectOptions(options: any = {}) {
    if (environment.features.feeData) {
      return this.getFeeData().pipe(
        map((d) => {
          options.maxFeePerGas = d.maxFeePerGas;
          options.maxPriorityFeePerGas = d.maxPriorityFeePerGas;
          return options;
        })
      );
    } else {
      return of(options);
    }
  }

  private getFeeData() {
    if (Date.now() - feeDataTimestamp > 3000) {
      return this.contractService.ethWalletService.getFeeData(true).pipe(
        tap((v) => {
          feeData = v;
          feeDataTimestamp = Date.now();
        })
      );
    } else {
      return of(feeData);
    }
  }
}
